import React, { memo } from 'react'
import type { MatrixCellStatus } from '@range-reader/shared'

interface MatrixCellProps {
  idx: number
  category: string
  comboCount: number
  status: MatrixCellStatus
  isFocused: boolean
  isActualHand: boolean
  isPair: boolean
  isSuited: boolean
  isOffsuit: boolean
  onFocus: (idx: number) => void
  inGtoRange?: boolean
}

// Flopzilla-style: pairs=gold, suited=pink, offsuit=lightblue
// Four states: HIGHLIGHTED (selected), REMOVED (soft off, re-addable), ELIMINATED (locked), actual hand (green)
const PALETTE = {
  pair:    { on: { bg: '#f0c040', text: '#1a1000', border: '#c8a030' },
             off: { bg: '#f5e8a0', text: '#8a7040', border: '#d4c060' } },
  suited:  { on: { bg: '#e87878', text: '#fff',    border: '#c05050' },
             off: { bg: '#f8d8d8', text: '#a05050', border: '#e8b0b0' } },
  offsuit: { on: { bg: '#7aaedc', text: '#fff',    border: '#4a88c0' },
             off: { bg: '#d8eaf8', text: '#4a6e90', border: '#a8cce8' } },
}

export const MatrixCell = memo(function MatrixCell({
  idx, category, comboCount, status, isFocused,
  isActualHand, isPair, isSuited, isOffsuit,
  onFocus, inGtoRange = false,
}: MatrixCellProps) {

  const type = isPair ? 'pair' : isSuited ? 'suited' : 'offsuit'

  let bg: string, text: string, border: string
  let opacity = 1
  let cursor = 'pointer'
  let strikethrough = false

  if (isActualHand) {
    bg = '#22c55e'; text = '#fff'; border = '#16a34a'
  } else if (status === 'ELIMINATED') {
    // Locked — greyed out, X mark
    bg = '#2a2a2a'; text = '#555'; border = '#333'
    cursor = 'not-allowed'
    strikethrough = true
  } else if (status === 'REMOVED') {
    // Soft removed — still tinted but dim, shows it can come back
    const col = PALETTE[type].off
    bg = col.bg; text = col.text; border = col.border
    opacity = 0.45
  } else if (status === 'HIGHLIGHTED') {
    const col = PALETTE[type].on
    bg = col.bg; text = col.text; border = col.border
  } else {
    // UNHIGHLIGHTED (cleared via toolbar)
    const col = PALETTE[type].off
    bg = col.bg; text = col.text; border = col.border
    opacity = 0.55
  }

  return (
    <div
      data-idx={idx}
      onMouseEnter={() => onFocus(idx)}
      title={`${category} — ${comboCount} combo${comboCount !== 1 ? 's' : ''}`}
      style={{
        backgroundColor: bg,
        color: text,
        opacity,
        outline: isFocused ? '2px solid #222' : 'none',
        outlineOffset: '-2px',
        zIndex: isFocused ? 10 : 1,
        cursor,
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        userSelect: 'none',
        aspectRatio: '1',
        borderRight: `1px solid ${border}`,
        borderBottom: `1px solid ${border}`,
        transition: 'opacity 0.1s',
      }}
      className="matrix-cell"
    >
      {inGtoRange && <span className="matrix-cell-gto-dot" />}
      <span className="matrix-cell-label" style={{ textDecoration: strikethrough ? 'line-through' : 'none' }}>
        {category}
      </span>
      {status === 'ELIMINATED' ? (
        <span className="matrix-cell-x">✕</span>
      ) : (
        <span className="matrix-cell-count">{comboCount}</span>
      )}
    </div>
  )
})
